import { wrapEntity } from "./entity";

export interface JsonPatchOp {
    op: "add" | "remove" | "replace" | "move" | "copy" | "test";
    path: string;
    value?: any;
    from_path?: string | null;
}

function unescapeSegment(segment: string): string {
    return segment.replace(/~1/g, "/").replace(/~0/g, "~");
}

function splitPointer(pointer: string): string[] {
    if (pointer === "") return [];
    return pointer.split("/").slice(1).map(unescapeSegment);
}

function resolveParent(root: any, segments: string[]): { parent: any; key: string } {
    let node = root;
    for (let i = 0; i < segments.length - 1; i++) {
        node = node?.[segments[i]];
    }
    return { parent: node, key: segments[segments.length - 1] };
}

function readValue(root: any, pointer: string): any {
    const segments = splitPointer(pointer);
    if (segments.length === 0) return root;
    const { parent, key } = resolveParent(root, segments);
    return parent?.[key];
}

// `insert` distinguishes RFC 6902 "add" (insert at index, shifting later
// elements up) from "replace" (overwrite in place). Getting this wrong for
// arrays silently diverges the client list from the server's model, which
// later surfaces as sparse `undefined` holes when a subsequent "add" writes
// past the (now too-short) array length.
function writeValue(root: any, pointer: string, value: any, insert: boolean): void {
    const segments = splitPointer(pointer);
    const { parent, key } = resolveParent(root, segments);
    if (parent == null) return;
    // `root` is always the Store instance applyPatch() was called on, and
    // chainPaths only ever grow by appending (see each *.generated.ts
    // constructor) -- so root._chainPath + this op's own path segments is
    // exactly the absolute chainPath a freshly-constructed entity at this
    // position would get, with no need to track which Store instance "owns"
    // whatever position this op writes to.
    if (Array.isArray(parent)) {
        const fieldSegments = segments.slice(0, -1);
        const idx = key === "-" ? parent.length : Number(key);
        const chainPath = [...root._chainPath, ...fieldSegments, String(idx)];
        const toWrite = wrapEntity(value, root._transport, chainPath);
        if (insert) {
            parent.splice(idx, 0, toWrite);
            // Inserting shifted every element after idx up one slot; restamp
            // their now-stale _chainPaths to match their new index (mirrors
            // removeValue).
            for (let i = idx + 1; i < parent.length; i++) {
                const item = parent[i];
                if (item != null && typeof item === "object" && "_chainPath" in item) {
                    item._chainPath = [...root._chainPath, ...fieldSegments, String(i)];
                }
            }
        } else {
            parent[idx] = toWrite;
        }
        return;
    }
    const chainPath = [...root._chainPath, ...segments];
    parent[key] = wrapEntity(value, root._transport, chainPath);
}

function removeValue(root: any, pointer: string): void {
    const segments = splitPointer(pointer);
    const { parent, key } = resolveParent(root, segments);
    if (parent == null) return;
    if (Array.isArray(parent)) {
        const index = Number(key);
        parent.splice(index, 1);
        // Every entity after the removed index just shifted down one slot -- its
        // _chainPath (baked in at construction/patch time, see writeValue) is now
        // stale and would route RPC calls to the wrong list position, so restamp it
        // to match its new index.
        const fieldSegments = segments.slice(0, -1);
        for (let i = index; i < parent.length; i++) {
            const item = parent[i];
            if (item != null && typeof item === "object" && "_chainPath" in item) {
                item._chainPath = [...root._chainPath, ...fieldSegments, String(i)];
            }
        }
    } else {
        delete parent[key];
    }
}

/** Applies one RFC 6902 JSON Patch op (as streamed by an observable pydantic
 * model's `stream_changes()`, see `common._ObservableMixin`) directly onto a
 * generated `*Store` instance or any of its deep-observable mobx fields. */
export function applyJsonPatch(root: any, op: JsonPatchOp): void {
    switch (op.op) {
        case "add":
            writeValue(root, op.path, op.value, true);
            return;
        case "replace":
            writeValue(root, op.path, op.value, false);
            return;
        case "remove":
            removeValue(root, op.path);
            return;
        case "move": {
            if (!op.from_path) return;
            const value = readValue(root, op.from_path);
            removeValue(root, op.from_path);
            writeValue(root, op.path, value, true);
            return;
        }
        case "copy": {
            if (!op.from_path) return;
            writeValue(root, op.path, readValue(root, op.from_path), true);
            return;
        }
        case "test":
            return;
    }
}
